import { Component, ElementRef, EventEmitter, Input, NgZone, Output, SimpleChanges, ViewChild } from '@angular/core';
import cloneDeep from 'lodash.clonedeep';
import { finalize } from 'rxjs/operators';
import Sortable, { SortableEvent } from 'sortablejs';

import { COLUMN_INFO_FIELD, COLUMN_METRIC } from 'Angular/constants/core.constants';
import { SavedItemType } from 'Angular/features/components/saved-items/enums/saved-item-type.enum';
import { StaticInfoMenuType } from 'Angular/services/static-menus/types/static-menu-type.type';
import { CalcSearchTypes } from 'CalculationsApp/ts/types/calc-search-types.type';
import { DataColumnInfo } from 'Angular/features/components/modals/edit-data-columns-modal/interfaces/data-column-info.interface';
import { StaticInfoFieldMenuItem } from 'Angular/services/static-menus/interfaces/static-info-field-menu.interface';
import { StaticMetricLabelMap } from 'Angular/services/static-menus/interfaces/static-metric-label-map.interface';
import { Metric } from 'CalculationsApp/ts/features/metric-selector-modal/interfaces/metric.interface';
import { CalcInfo } from 'CalculationsApp/ts/services/calculations-service.interface';
import { AutocompleteResult } from 'SearchApp/ts/interfaces/autocomplete-result.interface';
import { ShareableItem } from 'SharingApp/ts/interfaces/shareable-item.interface';

import { StaticMenuService } from 'Angular/services/static-menus/static-menus.service';
import { MetricsetModalService } from 'CalculationsApp/ts/features/metricset-modal/metricset-modal.service';
import { CalculationsService } from 'CalculationsApp/ts/services/calculations.service';
import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import equal from 'Angular/utils/equal/deep-equal';
import { disablePointerEvents, enablePointerEvents } from 'Angular/utils/sortable-utils';

/**
 *
 * Edit Data Columns Modal Component
 *
 * This modal allows users to reorder, add, and remove data columns. When this component is used on the dashboard,
 * the visible data columns in the security list module will be purple, and hidden columns will be grey. There's no way to tell
 * which data columns will be visible or hidden when the Data tab is not the active tab in the module, so all data columns will be purple in that case.
 *
 * Input Properties:
 *  allowInfoColumns: Whether info fields can be added as data columns
 *  submitButtonText: Text used for the submit button
 *  instructionText: Text used for instructions in the modal
 *  infoType: what type of info fields to allow, e.g. 'company', 'mutual_fund', 'all'
 *  metricSearchType: The type of search results to display in the add button autocomplete in the modal.
 *  maxMetricCols: The maximum number of metrics that can be added
 *  columns: Columns to be displayed in the modal
 *  defaultColumns: Default columns (displayed when the user clicks restore defaults button in the modal header)
 *  numVisibleColumns: Used on the dashboard. This determines how many data columns are purple in the modal, which designates
 *                     the data columns that will be visible in the security list module at its current width.
 *
 * Output Properties:
 *  submitHandler: EventEmitter that emits the ordered columns after they have been changed
 *  closeModalHandler: EventEmitter that emits when the modal is closed.
 */

@Component({
    standalone: false,
    selector: 'ycn-edit-data-columns-modal',
    templateUrl: './edit-data-columns-modal.template.html',
})
export class EditDataColumnsModalComponent {
    @ViewChild('dataContainer') dataContainer!: ElementRef;

    @Input() allowInfoColumns: boolean = false;

    @Input() submitButtonText?: string;

    @Input() instructionText!: string;

    @Input() infoType: StaticInfoMenuType = 'all';

    @Input() metricSearchType!: CalcSearchTypes;

    @Input() maxMetricCols: number = 10;

    @Input() columns!: DataColumnInfo[];

    @Input() defaultColumns!: DataColumnInfo[];

    @Input() numVisibleColumns: number = 0;

    @Output() submitHandler = new EventEmitter<DataColumnInfo[]>();

    @Output() closeModalHandler = new EventEmitter<void>();

    modalOpen: boolean = false;

    errors: string[] = [];

    calcsInfo: CalcInfo = {};

    orderedColumns: DataColumnInfo[] = [];

    metricSets: ShareableItem[] = [];

    metricLabelMap!: StaticMetricLabelMap;

    SAVED_ITEM_TYPE = SavedItemType.METRIC_SET;

    MODAL_HEADER_TEXT = 'Edit Columns';

    RESTORE_DEFAULTS_BUTTON_TEXT = 'Restore Defaults';

    showSortable: boolean = true;

    constructor(
        private ngZone: NgZone,
        private calculationsService: CalculationsService,
        private featureService: FeatureService,
        private staticMenuService: StaticMenuService,
        private metricsetModalService: MetricsetModalService
    ) {}

    ngOnInit(): void {
        this.submitButtonText = this.submitButtonText || 'Submit';
        this.staticMenuService.getStaticMenu('metricLabelMap', 'all').subscribe((metricLabels) => {
            this.metricLabelMap = metricLabels;
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.columns?.currentValue) {
            if (!this.columnsEqual(changes.columns.previousValue, changes.columns.currentValue)) {
                this.orderedColumns = cloneDeep(changes.columns.currentValue);
            }
        }
        if (changes.defaultColumns?.currentValue) {
            if (!this.columnsEqual(changes.defaultColumns.previousValue, changes.defaultColumns.currentValue)) {
                this.defaultColumns = cloneDeep(changes.defaultColumns.currentValue);
            }
        }
    }

    // Modal logic functions

    openModal(initialErrorMessage?: string): void {
        this.metricsetModalService.get().subscribe((response) => {
            this.metricSets = response;
        });

        this.initializeSortable();

        // if an initial error message is passed in, display it
        if (initialErrorMessage !== undefined) {
            this.errors.push(initialErrorMessage);
        }

        this.modalOpen = true;
    }

    closeModal(): void {
        this.clearErrors();
        this.modalOpen = false;
        this.closeModalHandler.emit();
        this.orderedColumns = cloneDeep(this.columns);
    }

    submit(): void {
        this.clearErrors();
        if (this.isOverMetricLimit) {
            this.errors.push(`Please reduce to a maximum of ${this.maxMetricCols} metrics.`);
            return;
        }
        // set ordered columns to default if all columns have been removed
        if (this.orderedColumns.length === 0) {
            this.restoreDefaults();
        }

        // if the columns haven't changed, close the modal
        if (this.columnsEqual(this.columns, this.orderedColumns)) {
            this.closeModal();
            return;
        }

        // if the columns have changed, validate the user has feature access to new columns
        const newlyAddedMetrics = this.getNewlyAddedMetrics();
        const metricsMissingInfo = newlyAddedMetrics.filter((calc) => !this.calcsInfo[calc]);
        if (metricsMissingInfo.length > 0) {
            this.calculationsService
                .getCalcInfo(metricsMissingInfo)
                .pipe(
                    finalize(() => {
                        // Regardless of whether the request succeeded or not, we still want to validate the calcs
                        this.checkFeaturesAndSubmit(newlyAddedMetrics);
                    })
                )
                .subscribe((response) => {
                    this.calcsInfo = { ...this.calcsInfo, ...response };
                });
        } else {
            this.checkFeaturesAndSubmit(newlyAddedMetrics);
        }
    }

    initializeSortable(): void {
        this.ngZone.runOutsideAngular(() => {
            Sortable.create(this.dataContainer.nativeElement, {
                handle: '.btn-reorder-up',
                scroll: false,
                animation: 150,
                onUpdate: (event: SortableEvent) => {
                    this.ngZone.run(() => {
                        this.updateOrdering(event.oldIndex!, event.newIndex!);

                        // This is a workaround for a bug that is reproduced by adding two items,
                        // dragging the top item to the bottom, and then adding another item. The items
                        // then will be out of order and the sorting will be broken
                        this.showSortable = false;
                        setTimeout(() => {
                            this.showSortable = true;
                            setTimeout(() => {
                                this.initializeSortable();
                            });
                        });
                    });
                },
                onStart: () => {
                    // Prevent pointer-events on btn classes on click in modules-container for tooltip/sortable bug
                    disablePointerEvents(document, 'options-list-item');
                },
                onEnd: () => {
                    enablePointerEvents(document, 'options-list-item');
                },
            });
        });
    }

    // Column update functions

    restoreDefaults(): void {
        this.clearErrors();
        this.orderedColumns = cloneDeep(this.defaultColumns);
    }

    removeColumn(columnIndex: number): void {
        const columnToRemove = this.orderedColumns[columnIndex];
        this.orderedColumns = this.orderedColumns.filter(
            (column) => column.metricInternalName !== columnToRemove.metricInternalName
        );
    }

    updateOrdering(oldIndex: number, newIndex: number): void {
        this.orderedColumns.splice(newIndex, 0, this.orderedColumns.splice(oldIndex, 1)[0]);
    }

    // Metric handlers

    /**
     * Display and handle an object selected from the single metric selector modal or from the autocompleter
     * @param metricObject - The metric object selected from the single metric selector modal
     * @param fromAutoComplete - Whether the metric was selected from the autocompleter
     */
    displayAndHandleModalObject(metricObject: Metric | AutocompleteResult, fromAutoComplete: boolean): void {
        if (metricObject !== undefined) {
            let label: string;
            let internalName: string;
            if (fromAutoComplete) {
                ({ name: label, search_id: internalName } = metricObject as AutocompleteResult);
            } else {
                ({ label, internal_name: internalName } = metricObject as Metric);
            }
            const featuresNeeded = metricObject.features_needed;

            // The feature needed modal is displayed when the user is missing the required
            // features, so no need to display an additional message
            if (!this.featureService.userCanAccess(metricObject)) return;
            this.generateMetricErrors(internalName!);
            if (this.errors.length !== 0) return;

            this.calcsInfo[internalName] = { label, features_needed: featuresNeeded };

            const newMetric: DataColumnInfo = {
                metricDisplayValue: label,
                metricInternalName: internalName,
                columnType: COLUMN_METRIC,
            };
            this.orderedColumns.push(newMetric);
        }
    }

    // Info column handler
    /**
     * Handles a field selected from the single info field selector modal.
     * @param infoField: field that was selected in the info field selector modal
     */
    addInfoColumn(infoField: StaticInfoFieldMenuItem): void {
        this.generateMetricErrors(infoField.key);
        if (this.errors.length !== 0) return;

        const newColumn: DataColumnInfo = {
            metricDisplayValue: infoField.name,
            metricInternalName: infoField.key,
            columnType: COLUMN_INFO_FIELD,
        };
        this.orderedColumns.push(newColumn);
    }

    // Metric set handler

    openMetricSet(metricSet: ShareableItem): void {
        const newColumns = (metricSet.calc_names || []).map((internal_name: string) => {
            return {
                metricDisplayValue: this.metricLabelMap?.[internal_name] ?? internal_name,
                metricInternalName: internal_name,
                columnType: COLUMN_METRIC,
            };
        });

        this.clearErrors();
        if (newColumns.length > this.maxMetricCols) {
            this.errors.push(`Metric set has more than the maximum of ${this.maxMetricCols} metrics.`);
            return;
        }
        this.orderedColumns = newColumns;
    }

    get isOverMetricLimit(): boolean {
        return this.orderedColumns.length > this.maxMetricCols;
    }

    // Helper functions

    /**
     * Returns the metrics that are in the current ordered columns but not in the input columns.
     * @private
     */
    private getNewlyAddedMetrics(): string[] {
        const currentMetrics = this.orderedColumns
            .filter((col) => col.columnType === COLUMN_METRIC)
            .map((col) => col.metricInternalName);
        const inputMetrics = this.columns
            .filter((col) => col.columnType === COLUMN_METRIC)
            .map((col) => col.metricInternalName);
        return currentMetrics.filter((metric) => !inputMetrics.includes(metric));
    }

    private checkFeaturesAndSubmit(newlyAddedMetrics: string[]): void {
        const noFeatureAccessMetrics = newlyAddedMetrics.filter((metric) => {
            return this.calcsInfo[metric] && !this.featureService.userCanAccess(this.calcsInfo[metric]);
        });
        if (noFeatureAccessMetrics.length > 0) {
            // if the user is missing features to any of the new metrics, display an error
            const calcLabels = noFeatureAccessMetrics.map((calc) => this.calcsInfo[calc].label || calc);
            this.errors.push(`The following items are not included in your subscription: ${calcLabels.join(', ')}`);
        } else {
            // otherwise, emit the new ordered columns
            this.submitHandler.emit(this.orderedColumns);
            this.closeModal();
        }
    }

    columnsEqual(columnsA: DataColumnInfo[], columnsB: DataColumnInfo[]): boolean {
        if (columnsA && columnsB) {
            const columnValuesA = columnsA.map((column) => column.metricInternalName);
            const columnValuesB = columnsB.map((column) => column.metricInternalName);
            return equal(columnValuesA, columnValuesB);
        }
        return false;
    }

    generateMetricErrors(newMetric: string): void {
        this.clearErrors();
        const cols = this.orderedColumns.map((column) => column.metricInternalName);

        if (cols.includes(newMetric)) {
            this.errors.push('This metric is already being used. Please select another metric.');
        }
        if (this.orderedColumns.length >= this.maxMetricCols) {
            this.errors.push(`Maximum of ${this.maxMetricCols} metrics reached.`);
        }
    }

    clearErrors(): void {
        this.errors = [];
    }
}
